import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, BookOpen, ChevronLeft, GraduationCap } from 'lucide-react';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { cardTheme } from '../../styles/cardTheme';

type TeacherSubject = {
  id: string;
  name: string;
  gradeName?: string;
  gradeId?: string;
  courseCount?: number;
};

type TeacherGrade = {
  gradeName: string;
  gradeId: string;
  subjects: TeacherSubject[];
  courseCount: number;
};

const GRADE_ORDER = [
  'الأول الإعدادي',
  'أولى إعدادي',
  'الثاني الإعدادي',
  'ثانية إعدادي',
  'الثالث الإعدادي',
  'ثالثة إعدادي',
  'الأول الثانوي',
  'أولى ثانوي',
  'الثاني الثانوي',
  'ثانية ثانوي',
  'الثالث الثانوي',
  'ثالثة ثانوي',
  'تالتة ثانوي',
];

function getGradeValue(gradeName?: string): string {
  if (!gradeName) return 'secondary1';
  if (gradeName.includes('الأول الثانوي') || gradeName.includes('أولى ثانوي')) return 'secondary1';
  if (gradeName.includes('الثاني الثانوي') || gradeName.includes('ثانية ثانوي')) return 'secondary2';
  if (gradeName.includes('الثالث الثانوي') || gradeName.includes('ثالثة ثانوي') || gradeName.includes('تالتة ثانوي')) {
    return 'secondary3';
  }
  if (gradeName.includes('الأول الإعدادي') || gradeName.includes('أولى إعدادي')) return 'prep1';
  if (gradeName.includes('الثاني الإعدادي') || gradeName.includes('ثانية إعدادي')) return 'prep2';
  if (gradeName.includes('الثالث الإعدادي') || gradeName.includes('ثالثة إعدادي')) return 'prep3';
  return 'secondary1';
}

function toGradeStateKey(gradeValue: string): string {
  const map: Record<string, string> = {
    secondary1: 'Secondary1',
    secondary2: 'Secondary2',
    secondary3: 'Secondary3',
    prep1: 'Prep1',
    prep2: 'Prep2',
    prep3: 'Prep3',
  };
  return map[gradeValue] ?? 'Secondary1';
}

function gradeSortIndex(gradeName: string): number {
  const idx = GRADE_ORDER.findIndex((label) => gradeName.includes(label) || label.includes(gradeName));
  return idx === -1 ? 999 : idx;
}

function groupSubjectsByGrade(subjects: TeacherSubject[]): TeacherGrade[] {
  const map = new Map<string, TeacherGrade>();

  subjects.forEach((subject) => {
    const gradeId = subject.gradeId || subject.gradeName || 'unknown';
    const gradeName = subject.gradeName || 'مرحلة غير محددة';
    const existing = map.get(gradeId);

    if (existing) {
      existing.subjects.push(subject);
      existing.courseCount += subject.courseCount ?? 0;
    } else {
      map.set(gradeId, {
        gradeId,
        gradeName,
        subjects: [subject],
        courseCount: subject.courseCount ?? 0,
      });
    }
  });

  return Array.from(map.values()).sort(
    (a, b) => gradeSortIndex(a.gradeName) - gradeSortIndex(b.gradeName)
  );
}

const PortalTeacherPage: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const [teacherName, setTeacherName] = useState('المدرس');
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(true);

  const grades = useMemo(() => groupSubjectsByGrade(subjects), [subjects]);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!teacherId) return;
      try {
        setLoading(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{ id: string; fullName: string; active: boolean }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        const found = response.data?.data?.find((t) => t.id === teacherId);
        if (found) setTeacherName(found.fullName);
      } catch {
        // keep default name
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [teacherId]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!teacherId) return;
      try {
        setLoadingGrades(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            name: string;
            gradeName?: string;
            gradeId?: string;
            courseCount?: number;
          }>;
        }>(`${BASE_URL}api/Teacher/${teacherId}/subjects`, {
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data.data) {
          setSubjects(
            response.data.data.map((s) => ({
              id: s.id,
              name: s.name,
              gradeName: s.gradeName,
              gradeId: s.gradeId,
              courseCount: s.courseCount,
            }))
          );
        } else {
          setSubjects([]);
        }
      } catch {
        setSubjects([]);
      } finally {
        setLoadingGrades(false);
      }
    };

    fetchSubjects();
  }, [teacherId]);

  const handleGradeClick = (grade: TeacherGrade) => {
    const gradeValue = getGradeValue(grade.gradeName);
    navigate(`/courses/grade/${gradeValue}`, {
      state: {
        grade: toGradeStateKey(gradeValue),
        teacherId,
        subjects: grade.subjects,
        fromStudentPortal: true,
      },
    });
  };

  return (
    <div className="pb-8" dir="rtl">
      <div className="border-b border-gray-100 bg-white p-4 sm:p-6 lg:p-8">
        <button
          type="button"
          onClick={() => navigate('/student-portal')}
          className="mb-4 flex min-h-11 items-center gap-2 text-gray-700 transition-colors hover:text-green-700"
        >
          <ArrowRight className="h-5 w-5" />
          <span className="text-sm font-medium sm:text-base">العودة للحصص والكورسات</span>
        </button>

        <div className={`mb-3 inline-flex items-center gap-2 ${cardTheme.badge}`}>
          <GraduationCap className="h-4 w-4" />
          مدرس
        </div>
        <h1 className={`mb-2 text-2xl font-bold sm:text-3xl ${cardTheme.title}`}>
          {loading ? 'جاري التحميل...' : teacherName}
        </h1>
        <p className={`max-w-2xl text-sm sm:text-base ${cardTheme.subtitle}`}>
          اختر المرحلة الدراسية لعرض كورسات هذا المدرس
        </p>
      </div>

      <section className="border-b border-gray-100 bg-gradient-to-b from-green-50/40 to-white py-6 sm:py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className={`mb-4 text-lg font-bold sm:mb-5 sm:text-xl ${cardTheme.title}`}>
            المراحل الدراسية
          </h2>

          {loadingGrades ? (
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-36 animate-pulse rounded-2xl bg-green-50/80 sm:h-40"
                />
              ))}
            </div>
          ) : grades.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {grades.map((grade, index) => (
                <button
                  key={grade.gradeId || index}
                  type="button"
                  onClick={() => handleGradeClick(grade)}
                  className={`group ${cardTheme.shell} ${cardTheme.shellHover} ${cardTheme.accentTop} flex min-h-[9.5rem] flex-col p-4 text-right sm:min-h-[10.5rem] sm:p-5`}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${
                        index % 2 === 0 ? cardTheme.iconBox : cardTheme.iconBoxGold
                      }`}
                    >
                      <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <ChevronLeft className="mt-1 h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-green-700" />
                  </div>

                  <h3
                    className={`mb-2 line-clamp-2 text-base font-bold leading-snug sm:text-lg ${cardTheme.title} group-hover:text-green-700`}
                  >
                    {grade.gradeName}
                  </h3>

                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-800 sm:text-xs">
                      <BookOpen className="h-3 w-3" />
                      {grade.subjects.length}{' '}
                      {grade.subjects.length === 1 ? 'مادة' : 'مواد'}
                    </span>
                    {grade.courseCount > 0 && (
                      <span className="inline-flex items-center rounded-full bg-gold-50 px-2.5 py-1 text-[11px] font-semibold text-gold-800 sm:text-xs">
                        {grade.courseCount} كورس
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className={`${cardTheme.shell} px-4 py-10 text-center`}>
              <GraduationCap className="mx-auto mb-3 h-12 w-12 text-gray-300" />
              <p className="text-sm text-gray-600 sm:text-base">
                لا توجد مراحل دراسية متاحة لهذا المدرس حالياً
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PortalTeacherPage;
